import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { DollarSignIcon } from '../../../../aida/components/svgs/icons/DollarSignIcon';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../../models/UiUtils';
import { brandPrimary, brandSecondary, charmGray, noteTintColor, success, warningDark } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { Attachments } from '../../Attachments';
import { DeprecatedPopover, PopoverType } from '../../DeprecatedPopover';
import { DefaultCompactEditorConfig } from '../../notes/NoteEditor';
import { RichContentDocumentEditor } from '../../richContent/RichContentDocumentEditor';
import { ActionItemsIcon } from '../../svgs/icons/ActionItemsIcon';
import { CalendarDateIcon } from '../../svgs/icons/CalendarDateIcon';
import { CallIcon } from '../../svgs/icons/CallIcon';
import { CheckmarkIcon } from '../../svgs/icons/CheckmarkIcon';
import { ClockIcon } from '../../svgs/icons/ClockIcon';
import { CustomSurveyIcon } from '../../svgs/icons/CustomSurvey';
import { DonationSurveyIcon } from '../../svgs/icons/DonationSurvey';
import { EmailIcon } from '../../svgs/icons/EmailIcon';
import { EventsSurveyIcon } from '../../svgs/icons/EventsSurvey';
import { NotesIcon } from '../../svgs/icons/NotesIcon';
import { PostageIcon } from '../../svgs/icons/PostageIcon';
import { QuestionMarkIcon } from '../../svgs/icons/QuestionMarkIcon';
import { SatisfactionSurveyIcon } from '../../svgs/icons/SatisfactionSurvey';
import { TextingIcon } from '../../svgs/icons/TextingIcon';
import { styleSheet } from './styles';
import { DownloadIconV2 } from '../../svgs/icons/DownloadIconV2';
import { PaperPlaneIcon } from '../../svgs/icons/PaperPlaneIcon';
import { TrashIcon } from '../../svgs/icons/TrashIcon';

export interface IEntityTimelineEventBaseProps<
	TTimelineEvent extends Api.ITimelineEvent = Api.ITimelineEvent,
	T extends Api.TimelineEventViewModel<TTimelineEvent> = Api.TimelineEventViewModel<TTimelineEvent>,
> {
	event: T;
}

export const EntityTimelineEventIcon: React.FC<IEntityTimelineEventBaseProps> = ({ event }) => {
	switch (event.type) {
		case 'SentEmailEvent':
		case 'ReceivedEmailEvent':
		case 'ViewedEmailEvent':
		case 'RepliedEmailEvent':
		case 'ExternalSentEmailEvent':
		case 'HtmlNewsletterEvent':
			return <EmailIcon fillColor={brandPrimary} />;
		case 'MeetingEvent':
		case 'MeetingScheduledEvent':
			return <CalendarDateIcon fillColor={brandSecondary} />;
		case 'DealCreatedEvent':
		case 'DealUpdatedEvent':
			return <DollarSignIcon className={css(styleSheet.dollarSignIcon)} style={{ color: brandSecondary }} />;
		case 'NoteEvent':
		case 'SkipLeadEvent':
			return <NotesIcon fillColor={noteTintColor} />;
		case 'ActionItemEvent':
			return <ActionItemsIcon fillColor={success} />;
		case 'ConversationThreadEvent':
			return <TextingIcon fill={warningDark} />;
		case 'PhoneCallEvent':
		case 'PhoneCallCompletedEvent':
		case 'UntrackedPhoneCallEvent':
			return <CallIcon fillColor={success} />;
		case 'FollowUpEvent':
		case 'CancelledFollowUpEvent':
		case 'RescheduledFollowUpEvent':
			return <ClockIcon fillColor={brandSecondary} />;
		case 'SatisfactionSurveyResponseEvent':
			return <SatisfactionSurveyIcon fillColor='#F3CE0D' />;
		case 'CustomSurveyResponseEvent':
			return <CustomSurveyIcon fillColor={brandPrimary} />;
		case 'DonationSurveyResponseEvent':
			return <DonationSurveyIcon fillColor='#E86C8D' />;
		case 'EventRegistrationSurveyResponseEvent':
			return <EventsSurveyIcon fillColor={success} />;
		case 'HandwrittenCardOrderEvent':
			return <PostageIcon fillColor={brandPrimary} />;
		case 'FileDownloadEvent':
			return <DownloadIconV2 color={brandPrimary} />;
		case 'FileDeleteEvent':
			return <TrashIcon fillColor={charmGray} width={24} height={24} />;
		case 'FileSendEvent':
			return <PaperPlaneIcon style={{ color: brandPrimary }} />;
		default:
			return null;
	}
};

export const EntityTimelineEventDateHeader: React.FC<{ date: Date }> = ({ date }) => {
	return (
		<div className={css(styleSheet.eventDateContainer)}>
			<div className={css(styleSheet.eventDate)}>{moment(date).format('dddd, MMMM Do, YYYY')}</div>
			<div className={css(styleSheet.eventDateFillLine)} />
		</div>
	);
};

export interface IEntityTimelineEventActionProps extends IEntityTimelineEventBaseProps {
	menu?: React.ReactNode;
}

export const EntityTimelineEventAction: React.FC<IEntityTimelineEventActionProps> = observer(({ event, menu }) => {
	const [toggleShowingTooltip, setToggleShowingTooltip] = React.useState(false);

	if (event.type === 'SentEmailEvent' || event.type === 'HtmlNewsletterEvent') {
		if (!(event as Api.SentEmailEventViewModel)?.viewmodel?.rawContentState) {
			return <span className={css(styleSheet.sentOutSideOfLev)}>Sent outside of Levitate</span>;
		}

		// render email viewed
		const emailEvent = event as Api.SentEmailEventViewModel;
		const openDate = emailEvent.openDate;
		const status = (emailEvent.note?.context as Api.IEmailMessageSendContext)?.status;

		return openDate ? (
			<>
				<div className={css(baseStyleSheet.flex)}>
					<div className={css(styleSheet.viewedOn)}>
						<span>
							<CheckmarkIcon className={css(baseStyleSheet.absoluteCenter)} fillColor='#fff' />
						</span>
						{status === Api.EmailTransactionStatus.MachineOpened ? 'Automatically opened' : 'Viewed'} on{' '}
						{moment(openDate).format('MM/DD/YYYY')}
					</div>
					{menu}
				</div>
				{status === Api.EmailTransactionStatus.MachineOpened ? (
					<div>
						<DeprecatedPopover
							className={css(styleSheet.popOver)}
							anchor={
								<div
									className={css(styleSheet.popOverIconWrap)}
									onMouseEnter={() => setToggleShowingTooltip(true)}
									onMouseLeave={() => setToggleShowingTooltip(false)}
								>
									<QuestionMarkIcon className={css(styleSheet.questionMarkIcon)} fillColor={brandPrimary} />
								</div>
							}
							contentClassName={css(styleSheet.popOverContentWrap)}
							dismissOnClickOutside={false}
							isOpen={toggleShowingTooltip}
							place='below'
							preferredPlacement='right'
							tipSize={0.01}
							type={PopoverType.gray}
						>
							<p className={css(styleSheet.popOverContent)}>
								This email was opened by an Apple device, we can&rsquo;t <br />
								determine if this contact has viewed the email.
							</p>
						</DeprecatedPopover>
					</div>
				) : null}
			</>
		) : (
			<div className={css(baseStyleSheet.flex)}>
				<div className={css(styleSheet.viewedOn)}>Email not viewed</div>
				{menu}
			</div>
		);
	} else if (event.type === 'NoteEvent' || event.type === 'ActionItemEvent' || event.type === 'PhoneCallEvent') {
		if (!(event as Api.NoteEventViewModel)?.viewmodel?.rawContentState) {
			return null;
		}
		return <>{menu}</>;
	}

	return null;
});

export const EntityTimelineEventTitle: React.FC<IEntityTimelineEventBaseProps> = ({ event }) => {
	let decoratedTitle: React.ReactNode = null;
	const hoverTitle: string = event.originalCreator ? `${event.title} created by ${event.originalCreator}` : event.title;

	/**
	 * External Associations Display Logic for Note Events
	 *
	 * BUSINESS REQUIREMENTS:
	 * 1. Display only ONE association to keep the UI clean and readable
	 * 2. Prioritize associations with timestamps (successful syncs with date info)
	 * 3. Alert users if ANY association failed, regardless of which one is displayed
	 *
	 * WHY THIS COMPLEXITY?
	 * - Users need to see successful sync information when available (timestamps are valuable)
	 * - But they also need to be alerted to any failures, even if from a different integration
	 * - Example: Redtail synced successfully (with date), Salesforce failed
	 *   → Show: "Synced with Redtail 07/29/25" + warning icon with Salesforce error
	 *
	 * This approach gives users both the most useful sync info AND failure awareness.
	 */
	if (event.type === 'NoteEvent') {
		const noteEvent = event as Api.NoteEventViewModel;
		const associations = noteEvent.viewmodel.externalAssociations;

		if (associations && associations.length > 0) {
			// DISPLAY LOGIC: Prioritize associations with timestamps (successful syncs with dates)
			// because this gives users the most valuable information about when data was last synced
			const associationToShow =
				associations.find((assoc: Api.IExternalAssociation) => assoc.timestamp) || associations[0];

			// FAILURE DETECTION: Check ALL associations for failures, not just the one we're displaying
			// This ensures users are alerted to ANY sync issues, even if we're showing a successful sync
			const hasFailure = associations.some((assoc: Api.IExternalAssociation) => assoc.failure);
			const failureMessage = associations.find((assoc: Api.IExternalAssociation) => assoc.failure)?.failure;

			const syncText = associationToShow.timestamp
				? ` - Synced with ${associationToShow.source} ${moment(associationToShow.timestamp).format('MM/DD/YY')}`
				: ` - Synced with ${associationToShow.source}`;

			decoratedTitle = (
				<div className={css(baseStyleSheet.truncateText)}>
					{event.title}
					<span className={css(styleSheet.externalAssociationInline)}>
						{syncText}
						{hasFailure && (
							<span className={css(styleSheet.errorIcon)} title={failureMessage}>
								⚠️
							</span>
						)}
					</span>
				</div>
			);
		}
	}

	if (event instanceof Api.SatisfactionSurveyResponseEventViewModel) {
		const surveyNameIndex = new RegExp(`${event.surveyName}`, 'igm').exec(event.title)?.index || -1;
		if (surveyNameIndex >= 0) {
			const ratingIndex = new RegExp(`${event.response.rating}$`, 'igm').exec(event.title)?.index || -1;

			let remainder = event.title.substring(surveyNameIndex + event.surveyName.length);
			if (ratingIndex >= 0) {
				remainder = remainder.substring(0, remainder.length - event.response.rating.toString().length);
			}
			decoratedTitle = (
				<>
					<span>{event.title.substring(0, surveyNameIndex)}</span>
					&nbsp;
					<span className={css(baseStyleSheet.truncateText, styleSheet.eventTitleSurveyName)} title={event.surveyName}>
						{`"${event.surveyName}"`}
					</span>
					&nbsp;
					{ratingIndex >= 0 ? (
						<span>
							{remainder}
							<span className={css(styleSheet.eventTitleSurveyRating)}>{event.response.rating}</span>
						</span>
					) : (
						<span>{remainder}</span>
					)}
				</>
			);
		}
	}

	if (event.type === 'CustomSurveyResponseEvent' || event.type === 'DonationSurveyResponseEvent') {
		const surveyEvent = event as Api.SurveyResponseEventViewModel;
		const surveyNameIndex = event.title?.indexOf(surveyEvent.surveyName);
		if (surveyNameIndex >= 0) {
			decoratedTitle = (
				<>
					<span>{event.title.substring(0, surveyNameIndex)}</span>
					<span
						className={css(baseStyleSheet.truncateText, styleSheet.eventTitleSurveyName)}
						title={surveyEvent.surveyName}
					>
						{`"${surveyEvent.surveyName}"`}
					</span>
				</>
			);
		}
	}

	if (event.type === 'EventRegistrationSurveyResponseEvent') {
		const eventData = event.toJs() as Api.IEventRegistrationSurveyResponseEvent;
		const surveyEvent = event as Api.SurveyResponseEventViewModel;
		if (eventData.response?.isAttending != null) {
			decoratedTitle = (
				<span>
					{event.originalCreator || 'Contact'} registered as{' '}
					<strong>{eventData.response.isAttending ? 'Attending' : 'Not Attending'}</strong> to the event{' '}
					<span
						className={css(baseStyleSheet.truncateText, styleSheet.eventTitleSurveyName)}
						title={surveyEvent.surveyName}
					>
						{`"${surveyEvent.surveyName}"`}
					</span>
				</span>
			);
		}
	}

	return (
		<div className={`entity-title ${css(baseStyleSheet.truncateText, styleSheet.eventTitle)}`} title={hoverTitle}>
			{decoratedTitle || <div className={css(baseStyleSheet.truncateText)}>{event.title}</div>}
		</div>
	);
};

const SatisfactionSurveyContent: React.FC<{ response: Api.ISatisfactionSurveyResponse }> = ({ response }) => {
	const [expanded, setExpanded] = React.useState(false);

	React.useEffect(() => {
		setExpanded(false);
	}, [response?.feedback]);

	const feedbackText = response?.feedback;
	const words = feedbackText?.split(' ');
	const isLong = words && words.length > 100;
	const collapsed = isLong ? words.slice(0, 100).join(' ') : null;

	const hasFeedback = !!feedbackText;
	const hasCustomProps = response?.customProperties && Object.keys(response.customProperties).length > 0;

	if (!hasFeedback && !hasCustomProps) {
		return null;
	}

	return (
		<div className={css(styleSheet.satisfactionSurveyResponseEventContent)}>
			{hasFeedback && (
				<div className={css(styleSheet.satisfactionSurveyResponseEventRow)}>
					<div className={css(styleSheet.satisfactionSurveyResponseEventContentLabel)}>Feedback:</div>
					&nbsp;
					{!collapsed || expanded ? (
						<div className={css(baseStyleSheet.breakWord, styleSheet.satisfactionSurveyResponseEventFeedback)}>
							{`"${feedbackText}"`}
						</div>
					) : (
						<div className={css(baseStyleSheet.breakWord)}>
							{`"${collapsed}..."`}
							&nbsp;
							<button className={css(baseStyleSheet.brandLink)} onClick={() => setExpanded(true)}>
								Read more
							</button>
						</div>
					)}
				</div>
			)}
			{hasCustomProps &&
				Object.entries(response.customProperties).map(([key, value], index) => (
					<div key={`${index}-${key}`} className={css(styleSheet.satisfactionSurveyResponseEventRow)}>
						<div className={css(styleSheet.satisfactionSurveyResponseEventContentLabel)}>
							{key.length > 80 ? `${key.substring(0, 80)}...` : key}:
						</div>
						&nbsp;
						<div className={css(baseStyleSheet.breakWord)}>
							{typeof value === 'string' ? value || 'N/A' : 'Attachment'}
						</div>
					</div>
				))}
		</div>
	);
};

export const EntityTimelineSurveyResponseEventContent: React.FC<
	IEntityTimelineEventBaseProps<Api.ISurveyResponseEvent, Api.SurveyResponseEventViewModel>
> = ({ event }) => {
	const data = event.toJs();
	const response = data.response;

	if (data._type === 'SatisfactionSurveyResponseEvent') {
		return <SatisfactionSurveyContent response={response as Api.ISatisfactionSurveyResponse} />;
	}

	if (data._type === 'CustomSurveyResponseEvent') {
		const customResponse = (data as Api.ICustomSurveyResponseEvent).response;
		const props = customResponse?.customProperties;
		if (!props || Object.keys(props).length === 0) {
			return null;
		}
		return (
			<div className={css(styleSheet.satisfactionSurveyResponseEventContent)}>
				{Object.entries(props).map(([key, value], index) => (
					<div key={`${index}-${key}`} className={css(styleSheet.satisfactionSurveyResponseEventRow)}>
						<div className={css(styleSheet.satisfactionSurveyResponseEventContentLabel)}>
							{key.length > 80 ? `${key.substring(0, 80)}...` : key}:
						</div>
						&nbsp;
						<div className={css(baseStyleSheet.breakWord)}>{typeof value === 'string' ? value : 'Attachment'}</div>
					</div>
				))}
			</div>
		);
	}

	if (data._type === 'DonationSurveyResponseEvent') {
		const donationResponse = (data as Api.IDonationSurveyResponseEvent).response;
		return (
			<div className={css(styleSheet.satisfactionSurveyResponseEventContent)}>
				<div className={css(styleSheet.satisfactionSurveyResponseEventRow)}>
					<div className={css(styleSheet.satisfactionSurveyResponseEventContentLabel)}>Amount:</div>
					&nbsp;
					<div>
						{donationResponse?.donationAmount != null
							? `$${(donationResponse.donationAmount / 100).toFixed(2)}`
							: 'N/A'}
					</div>
				</div>
				{donationResponse?.selectedCampaign?.name && (
					<div className={css(styleSheet.satisfactionSurveyResponseEventRow)}>
						<div className={css(styleSheet.satisfactionSurveyResponseEventContentLabel)}>Campaign:</div>
						&nbsp;
						<div>{donationResponse.selectedCampaign.name}</div>
					</div>
				)}
				{donationResponse?.payments?.[0]?.status && (
					<div className={css(styleSheet.satisfactionSurveyResponseEventRow)}>
						<div className={css(styleSheet.satisfactionSurveyResponseEventContentLabel)}>Payment:</div>
						&nbsp;
						<div>{donationResponse.payments[0].status}</div>
					</div>
				)}
			</div>
		);
	}

	if (data._type === 'EventRegistrationSurveyResponseEvent') {
		const regResponse = (data as Api.IEventRegistrationSurveyResponseEvent).response;
		const guestCount = (regResponse?.guests?.length ?? 0) + (regResponse?.anonymousGuestCount ?? 0);
		return (
			<div className={css(styleSheet.satisfactionSurveyResponseEventContent)}>
				{regResponse?.attendee && (
					<div className={css(styleSheet.satisfactionSurveyResponseEventRow)}>
						<div className={css(styleSheet.satisfactionSurveyResponseEventContentLabel)}>Attendee:</div>
						&nbsp;
						<div>
							{regResponse.attendee.firstName} {regResponse.attendee.lastName}
						</div>
					</div>
				)}
				{guestCount > 0 && (
					<div className={css(styleSheet.satisfactionSurveyResponseEventRow)}>
						<div className={css(styleSheet.satisfactionSurveyResponseEventContentLabel)}>Guests:</div>
						&nbsp;
						<div>{guestCount}</div>
					</div>
				)}
				{regResponse?.confirmationNumber && (
					<div className={css(styleSheet.satisfactionSurveyResponseEventRow)}>
						<div className={css(styleSheet.satisfactionSurveyResponseEventContentLabel)}>Confirmation:</div>
						&nbsp;
						<div>{regResponse.confirmationNumber}</div>
					</div>
				)}
			</div>
		);
	}

	return null;
};

export const EntityTimelineNoteEventContent = ({ note }: { note: Api.NoteEventViewModel }) => {
	return (
		<div className={css(styleSheet.eventNoteContainer)}>
			<RichContentDocumentEditor
				autoFocus={false}
				className='note-editor-body-editor'
				config={DefaultCompactEditorConfig}
				contentState={convertRawRichTextContentStateToRichContentEditorState(note.viewmodel.rawContentState)}
				readOnly={true}
			/>
			{note.viewmodel.attachments ? <Attachments attachments={note.note.attachments} /> : null}
		</div>
	);
};
